import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function ApproveAppointment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [message, setMessage] = useState('Processing your approval...');
  const [appointmentDetails, setAppointmentDetails] = useState(null);

  useEffect(() => {
    const processApproval = async () => {
      try {
        const token = searchParams?.get('token');
        
        if (!token) {
          setStatus('error');
          setMessage('Invalid approval link. Token is missing.');
          return;
        }

        // Find appointment by approval token
        const { data: appointment, error: fetchError } = await supabase
          ?.from('appointments')
          ?.select('*')
          ?.eq('approval_token', token)
          ?.single();

        if (fetchError || !appointment) {
          setStatus('error');
          setMessage('Appointment not found or link has expired.');
          return;
        }

        // Check if already processed
        if (appointment?.status !== 'pending') {
          setStatus('error');
          setMessage(`This appointment has already been ${appointment?.status}.`);
          setAppointmentDetails(appointment);
          return;
        }

        // Update appointment status to approved
        const { error: updateError } = await supabase
          ?.from('appointments')
          ?.update({ 
            status: 'approved',
            approved_at: new Date()?.toISOString(),
            approved_by_email: appointment?.bail_agent_id ? 'agent@sailorsbail.com' : 'system@sailorsbail.com',
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', appointment?.id);

        if (updateError) {
          console.error('Error updating appointment:', updateError);
          setStatus('error');
          setMessage('Failed to approve appointment. Please try again or contact support.');
          return;
        }

        // Success
        setStatus('success');
        setMessage('Appointment approved successfully!');
        setAppointmentDetails(appointment);

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/appointment-management-dashboard');
        }, 3000);

      } catch (err) {
        console.error('Approval processing error:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please contact support.');
      }
    };

    processApproval();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-center">
            <Shield className="w-16 h-16 text-white mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white">
              Appointment Approval
            </h1>
          </div>

          {/* Content */}
          <div className="p-8">
            {status === 'processing' && (
              <div className="text-center py-12">
                <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-xl text-gray-700">{message}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {message}
                </h2>
                {appointmentDetails && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-left">
                    <h3 className="font-semibold text-gray-900 mb-3">Appointment Details:</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Client:</strong> {appointmentDetails?.client_name}</p>
                      <p><strong>Date:</strong> {new Date(appointmentDetails.appointment_date)?.toLocaleString()}</p>
                      <p><strong>Purpose:</strong> {appointmentDetails?.purpose}</p>
                    </div>
                  </div>
                )}
                <p className="text-gray-600">
                  The client has been notified. Redirecting to dashboard...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-12">
                <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Unable to Process Approval
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  {message}
                </p>
                {appointmentDetails && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left">
                    <h3 className="font-semibold text-gray-900 mb-3">Appointment Details:</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Client:</strong> {appointmentDetails?.client_name}</p>
                      <p><strong>Date:</strong> {new Date(appointmentDetails.appointment_date)?.toLocaleString()}</p>
                      <p><strong>Status:</strong> <span className="capitalize">{appointmentDetails?.status}</span></p>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => navigate('/appointment-management-dashboard')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Sailors Bail Bonds - Professional Bail Services
          </p>
        </div>
      </div>
    </div>
  );
}